import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import styled from "styled-components";
import { font } from "../../constants/font";
import Typography from "../../common-components/Typography";
import vmTheme from "../../constants/theme";
import { secondsToMinute } from "../timeline/timeline-helper";
import AppConfig, {
  API,
  API_URL,
  ENVIRONMENT,
  SITE_URL,
  STATIC_PATH,
  THUMBNAIL_URL,
} from "../../constants/config";
import {
  thumbnailGeneration,
  preloadExtractorVideo,
} from "../../helper/shortsHelper";
import { getShortReframe } from "./shorts-helper";
import { getWorkspaceItemSource } from "../../helper/URLHelper";
import { Links, SocialMedia } from "./shorts-components";
import useApi from "../../helper/hooks/useApi";
import openUrl from "../../helper/openUrl";
import { useShortsContext } from "./shortsContextApi";
import useUpgrade from "../../helper/hooks/useUpgrade";
import content from "../../constants/content";
import CustomTooltip from "../../common-components/CustomTooltip";
import ShortsFeedbackButtons from "./shorts-feedback-buttons";
import TRACKERS from "../../constants/trackers";
import trackerEvents from "../../helper/trackerEvents";
import { PLAN } from "../../constants";
import { getLockedCardContent } from "../../constants/shortsLockedCardContent";
import Action from "../../common-components/Action";

const SHORT_CARD_LAYOUT = {
  horizontal: { className: "hor", aspectRatio: "16 / 9", width: "354px" },
  vertical: { className: "ver", aspectRatio: "9 / 16", width: "240px" },
  square: { className: "sqr", aspectRatio: "1 / 1", width: "256px" },
};

const getShortCardLayout = (dimensionName) =>
  SHORT_CARD_LAYOUT[dimensionName] || SHORT_CARD_LAYOUT.vertical;

const ShortsListScrollArea = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  * {
    touch-action: auto;
  }

  @media (max-width: 767px) {
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y !important;
    * {
      touch-action: auto;
    }
    overscroll-behavior: contain;
    will-change: scroll-position;
    scroll-padding-bottom: 24px;
  }
`;

const ShortsGridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
  justify-content: start;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  width: 100%;

  @media (max-width: 1189px) {
    justify-content: center;
    gap: 12px;
  }

  @media (max-width: 767px) {
    gap: 8px;
    justify-content: center;
    padding: 8px 8px 16px;
    touch-action: pan-y;
    * {
      touch-action: auto;
    }
  }
`;

const ShortCard = styled.div`
  position: relative;
  background: ${(props) => props.theme.subtitleBgColor};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  width: ${(props) => props.$width};
  aspect-ratio: ${(props) => props.$aspectRatio};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  touch-action: manipulation;

  @media (max-width: 767px) {
    width: 100%;
    max-width: ${(props) => props.$width};
  }

  &:hover {
    box-shadow: ${(props) =>
    props.isLocked ? "unset" : "0px 8px 16px rgba(0, 0, 0, 0.2)"};
  }

  &:hover .shorts-card-feedback {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`;

const ThumbnailContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.blackColor};
  overflow: hidden;
  filter: ${(props) => (props.$isBlurred ? "blur(6px)" : "none")};
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  /* Always on top — video only shows when fully ready at correct frame */
  opacity: ${(props) => (props.$isHidden ? 0 : 1)};
  transition: opacity 0.15s ease;
  z-index: 2;
`;

// Hover play — disabled for now
const PlayButtonOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  img {
    width: 40px;
    height: 40px;
  }
`;

const CardFeedbackOverlay = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const LockIconOverlay = styled.div`
  position: absolute;
  top: 42%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;

  img {
    width: 28px;
    height: 28px;
  }
`;

const LockedCard = styled.div`
  position: absolute;
  top: ${props => props.projectSize === 'hor' ? '35%' : props.projectSize === 'sqr' ? '37%' : '42%'};
  left: 50%;
  transform: translate(-50%, -50%);
  background: #ffffff;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.32);
  padding: ${props => props.projectSize === 'hor' ? '12px ' : '20px'};
  width: 84%;
  max-width: 260px;
  min-height: ${props => props.projectSize === 'hor' ? 'auto' : 'auto'};
  text-align: center;
  pointer-events: auto;
  z-index: 1;
`;

const LockedIcon = styled.img`
  font-size: 32px;
  margin: ${props => props.margin === 'hor' ? '20px 0px 0px' : props.margin === 'sqr' ? '12px 0px 0px' : '24px 0px 0px'};
`;

const CARD_META_HEIGHT = "48px";

const CardMetaSection = styled.div`
  width: 100%;
  height: ${CARD_META_HEIGHT};
  min-height: ${CARD_META_HEIGHT};
  margin-top: 8px;
  padding: 0 2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const SkeletonLoader = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 25%,
    #e0e0e0 75%,
    #d0d0d0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ShortCardContainer = styled.div`
  position: relative;
  border-radius: 12px;
  padding: 12px;
  width: fit-content;
  background: ${(props) => (props.$isViewed ? "#dfe1e5" : "transparent")};
  touch-action: manipulation;
  display: flex;
  flex-direction: column;
`;

export const FlexDiv = styled.div`
  padding: 0px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.justifyContent || "space-between"};
  gap: ${(props) => props.gap || "0px"};
  .subtitle-btn {
    position: relative;
    background: var(--polarColor);
    &:hover {
      background: var(--shortsBtnBgColor);
    }
    &::before {
      // content: url(${STATIC_PATH}subtitle.svg);
      // padding: 12px;
      // top: 1px;
      // position: relative;
    }
    &.is-loading {
      padding: 0;
      min-width: 36px;
      overflow: hidden;
      &::before {
        display: none;
      }
    }
  }
  .edit-project {
    position: relative;
    // padding-left: 8px;
    background: var(--polarColor);
    &:hover {
      background: var(--shortsBtnBgColor);
    }
    &::before {
      // content: url(${STATIC_PATH}vm-cut.svg);
      // padding: 12px;
      // top: 1px;
      // position: relative;
    }
    &.is-loading {
      padding: 0;
      min-width: 36px;
      overflow: hidden;
      &::before {
        display: none;
      }
    }
  }
  .export-project {
    position: relative;
    padding-left: 8px;
    &:hover {
      background: ${(props) => props.theme.shortsBtnHoverColor};
    }
    &::before {
      content: url(${STATIC_PATH}vm-upload.svg);
      position: absolute;
      left: 7px;
      top: 7px;
    }
    &.is-loading {
      padding-left: 0;
      min-width: 36px;
      overflow: hidden;
      &::before {
        display: none;
      }
    }
  }
`;
const ShortInfoFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CutIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }

  img {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }
`;

const CardLoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
`;

const CardLoaderSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: cardLoaderSpin 0.8s linear infinite;

  @keyframes cardLoaderSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;
export const Divider = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${(props) => props.theme.shortsBorderColor};
`;
const EmptyContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  height: calc(100vh - 160px);
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;
  color: ${(props) => props.theme.primaryColor};
  text-align: center;
  touch-action: pan-y;
  * {
    touch-action: auto;
  }

  @media (max-width: 767px) {
    touch-action: pan-y !important;
    * {
      touch-action: auto;
    }
  }
`;

const StickyFooterBanner = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin: 0 auto;
  padding-bottom: 12px;
  box-sizing: border-box;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px;
  }

  .regenerate-shorts-button {
    img {
      position: relative !important;
    }

    label {
      position: relative;
      width: 198px;

      &:after {
        content: "pro";
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        padding: 2px 8px;
        background: #5028ff;
        border-radius: 50px;
        color: #fff;
        text-transform: uppercase;
        font: ${font.bold_12_18};
      }
    }

    @media (max-width: 767px) {
      width: fit-content;
      padding: 0 12px;
    }
  }
`;

const FooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// ShortCardItem
// ─────────────────────────────────────────────────────────────────────────────

const ShortCardItem = React.memo(
  ({
    short,
    onSelectShort,
    videoSrc,
    index,
    isViewed,
    feedbackType,
    isFeedbackLoaded,
    onLike,
    onDislike,
    isLocked: isLockedFromPlan,
  }) => {
    // const slotRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);
    // const [videoReady, setVideoReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const theme = useSelector((state) => state.app.get("theme"));
    const { post } = useApi();
    const { showPricingModal } = useUpgrade();
    const userDetails = useSelector((state) => state.userDetails);
    const { projectId, permission, dimensionName } = userDetails;
    const sourceType = userDetails?.sourceType;
    const cardLayout = getShortCardLayout(dimensionName);

    const presetThumbnailUrl = useMemo(() => {
      const rawThumb = short.get?.("thumbnail_url") ?? short.thumbnail_url;
      if (rawThumb) {
        return rawThumb.startsWith("http") ? rawThumb : `${THUMBNAIL_URL}${rawThumb}`;
      }
      return null;
    }, [short]);

    const dummyFrameThumbnailUrl = useMemo(() => {
      if (sourceType === "shorts-dummy" && userDetails?.frames?.length > 0) {
        return `${THUMBNAIL_URL}${userDetails.frames[index % userDetails.frames.length]
          }`;
      }
      return null;
    }, [sourceType, userDetails?.frames, index]);

    const [frameThumb, setFrameThumb] = useState(
      presetThumbnailUrl || dummyFrameThumbnailUrl,
    );
    const [isThumbLoading, setIsThumbLoading] = useState(
      !presetThumbnailUrl && !dummyFrameThumbnailUrl,
    );
    const [thumbFailed, setThumbFailed] = useState(false);


    useEffect(() => {
      if (short.get("thumbnail_url")) {
        setFrameThumb(presetThumbnailUrl);
        setIsThumbLoading(false);
      }
    }, [short.get("thumbnail_url")]);

    const {
      title: shortTitle,
      start: shortStart,
      end: shortEnd,
    } = useMemo(() => {
      if (short.get) {
        return {
          title: short.get("title"),
          start: short.get("start"),
          end: short.get("end"),
        };
      }
      return {
        title: short.title,
        start: short.start,
        end: short.end,
      };
    }, [short]);

    const shortData = useMemo(
      () => (short.get ? short.toJS() : short),
      [short],
    );

    const aspectRatio = useMemo(() => {
      const [w, h] = cardLayout.aspectRatio
        .split("/")
        .map((s) => parseFloat(s.trim()));
      return w / h;
    }, [cardLayout.aspectRatio]);

    const reframe = useMemo(() => getShortReframe(shortData, shortStart), [shortData, shortStart]);

    const confidenceScore = short.get ? short.get("confidenceScore") : short.confidenceScore;

    const socialMedia = short.get
      ? (short.get("socialMedia") || []).toArray?.() || short.get("socialMedia") || []
      : short.socialMedia || [];

    // Lock logic: locked only for shorts-dummy (skip for no-audio / no-transcription)
    const shouldSkipLock =
      sourceType === "Shorts-No-Audio" ||
      sourceType === "Short-No-Transcription";
    const isLocked =
      (sourceType === "shorts-dummy" || isLockedFromPlan) && !shouldSkipLock;
    const canShowThumbnail = isFeedbackLoaded;
    const shortId = short.get ? short.get("id") : short.id;

    const generateFrameThumb = useCallback(() => {
      if (!videoSrc || !Number.isFinite(shortStart)) {
        setIsThumbLoading(false);
        setThumbFailed(true);
        return undefined;
      }

      setIsThumbLoading(true);
      setThumbFailed(false);

      let cancelled = false;
      thumbnailGeneration(videoSrc, shortStart, { reframe, aspectRatio })
        .then((frameUrl) => {
          if (cancelled) return;
          setIsThumbLoading(false);
          if (frameUrl) {
            setFrameThumb(frameUrl);
          } else {
            setThumbFailed(true);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error(
              `[ShortCardItem ${index}] Error generating frame thumbnail at:`,
              performance.now(),
              err,
            );
            setIsThumbLoading(false);
            setThumbFailed(true);
          }
        });

      return () => {
        cancelled = true;
      };
    }, [videoSrc, shortStart, index, reframe, aspectRatio]);

    useEffect(() => {
      if (!canShowThumbnail) {
        setFrameThumb(null);
        setIsThumbLoading(!isFeedbackLoaded);
        setThumbFailed(false);
        return undefined;
      }

      if (presetThumbnailUrl) {
        setFrameThumb(presetThumbnailUrl);
        setIsThumbLoading(false);
        return undefined;
      }

      if (dummyFrameThumbnailUrl) {
        setFrameThumb(dummyFrameThumbnailUrl);
        setIsThumbLoading(false);
        return undefined;
      }

      setFrameThumb(null);
      setIsThumbLoading(true);
      setThumbFailed(false);

      const cleanupGenerate = generateFrameThumb();

      return () => {
        cleanupGenerate?.();
      };
    }, [
      videoSrc,
      shortStart,
      index,
      generateFrameThumb,
      presetThumbnailUrl,
      dummyFrameThumbnailUrl,
      canShowThumbnail,
      isFeedbackLoaded,
      reframe,
      aspectRatio,
    ]);

    const handleThumbError = useCallback(() => {
      setFrameThumb(null);
      if (!videoSrc) {
        setThumbFailed(true);
        setIsThumbLoading(false);
        return;
      }
      generateFrameThumb();
    }, [videoSrc, generateFrameThumb]);

    const handleMouseEnter = useCallback(() => {
      setIsHovering(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovering(false);
    }, []);

    const handleClick = useCallback(() => {
      if (isLoading) return;
      // If short is locked, show pricing modal
      if (isLocked) {
        showPricingModal({
          type: "longtoshort",
          source: TRACKERS.LFTS_LOCKED_UPGRADE_CLICK,
          shortId,
        });
        trackerEvents(TRACKERS.LFTS_LOCKED_UPGRADE_CLICK, {
          projectId: userDetails.projectId,
          shortId,
          source: "locked-shorts",
          isBoth: 1,
        });
        return;
      }
      // Otherwise, select the short normally
      if (onSelectShort) onSelectShort(short);
    }, [
      isLoading,
      isLocked,
      onSelectShort,
      short,
      shortId,
      showPricingModal,
      userDetails.projectId,
    ]);

    const openNewProject = useCallback(
      (shortId, isExport = false) => {
        if (isLoading) return;
        if (!isExport) {
          trackerEvents(TRACKERS.LTSF_PROJECT_EDITED, {
            projectId,
            shortId,
            isBoth: 1,
          });
        }
        let pathName = "";
        if (isExport) {
          pathName = "render/";
        } else {
          pathName = ENVIRONMENT === "LOCAL" ? "" : "editproject/";
          setIsLoading(true);
        }
        post(`${API_URL}${API.SHORTS.CREATE}`, {
          projectId,
          shortId,
          isExport,
        })
          .then((res) => {
            if (!res.error) {
              openUrl(
                `${SITE_URL}${pathName + res.projectId}${isExport ? `?shortId=${shortId}` : ""
                }`,
              );
            }
            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
          });
      },
      [isLoading, projectId, post],
    );
    const debugId = short?.get?.("debug_id") || "";
    const [label = ""] = debugId.split("_");

    const formattedDebug = `${label.charAt(0).toUpperCase() + label.slice(1)}`;

    // Use dummy title if no title available
    const displayTitle =
      shortTitle ||
      (formattedDebug
        ? `${formattedDebug} Clip ${index + 1}`
        : `Short ${index + 1}`);

    const lockedCardContent = useMemo(() => {
      const baseContent = getLockedCardContent(shortId);
      // Replace title with actual confidence score if available
      if (confidenceScore && baseContent.id === "ai-pick") {
        return {
          ...baseContent,
          title: `${Math.round(confidenceScore)}% Viral Score`,
        };
      }
      return baseContent;
    }, [shortId, confidenceScore]);

    return (
      <ShortCardContainer className={cardLayout.className} $isViewed={isViewed}>
        {isLocked && sourceType === "shorts-dummy" && (
          <LockIconOverlay onClick={handleClick}>
            <img src={`${STATIC_PATH}orange-lock.svg`} alt="Locked" />
          </LockIconOverlay>
        )}
        {isLocked && sourceType !== "shorts-dummy" && (
          <LockedCard onClick={handleClick} projectSize={cardLayout.className}>
            <Typography
              innerContent={lockedCardContent.badge}
              font={font.boldMicro}
              color="#2C7794"
              textStyles={{
                background: "#CCF1FF",
                padding: "4px 8px",
                borderRadius: "50px",
                position: "absolute",
                top: "8px",
                right: "8px",
                marginBottom: "8px",
              }}
            />
            <LockedIcon
              src={`${STATIC_PATH}${lockedCardContent.icon}`}
              alt="Locked"
              margin={cardLayout.className}
            />
            <Typography
              innerContent={lockedCardContent.title}
              font={font.boldMini_13}
              padding="4px 0px"
              color="#5028FF"
              display={"block"}
              align={"center"}
            />
            <Typography
              innerContent={lockedCardContent.description}
              color="#4C5061"
              font={font.normal_medium_12_14}
              display='block'
              padding={cardLayout.className === 'hor' ? '2px 0px' : cardLayout.className === 'sqr' ? '2px 0px' : '4px 0px'}
              enableTrim={false}
              align={"center"}
            />
            <Action
              text="Upgrade to unlock"
              onClick={handleClick}
              padding="0px 4px 0px 16px"
              margin={cardLayout.className ? "6px 0px 0px" : "16px 0px"}
              font={font.boldMini_14}
              background={"linear-gradient(90deg, #FF7F25 0%, #F55322 100%)"}
              hoverColor={"linear-gradient(90deg, #F55322 0%, #FF7F25 100%)"}
              borderRadius={"50px"}
            />
          </LockedCard>
        )}
        <ShortCard
          $width={cardLayout.width}
          isLocked={isLocked}
          $aspectRatio={cardLayout.aspectRatio}
          onClick={handleClick}
          onMouseEnter={isLocked ? undefined : handleMouseEnter}
          onMouseLeave={isLocked ? undefined : handleMouseLeave}
          role="button"
          tabIndex={0}
        >
          <ThumbnailContainer $isBlurred={isLocked}>
            {(!isFeedbackLoaded || isThumbLoading) && <SkeletonLoader />}
            {canShowThumbnail && frameThumb ? (
              <Thumbnail
                src={frameThumb}
                alt={displayTitle}
                onError={handleThumbError}
              />
            ) : canShowThumbnail && thumbFailed && !isThumbLoading ? (
              <Thumbnail
                src={`${STATIC_PATH}defaultplaceholder.svg`}
                alt="Dummy thumbnail"
              />
            ) : null}
            {!isLocked && permission !== 0 && (
              <CardFeedbackOverlay className="shorts-card-feedback">
                <ShortsFeedbackButtons
                  feedbackType={feedbackType}
                  tooltipIdSuffix={shortId}
                  tooltipContext="card"
                  variant="onDark"
                  onLike={() => {
                    trackerEvents(TRACKERS.LFSF_VIDEO_LIKED, {
                      projectId,
                      shortId,
                      isBoth: 1,
                    });
                    onLike?.(shortId);
                  }}
                  onDislike={() => {
                    onDislike?.(shortId);
                  }}
                />
              </CardFeedbackOverlay>
            )}
            {!isLocked && isHovering && (
              <PlayButtonOverlay>
                <img src={`${STATIC_PATH}play-button.svg`} alt="" />
              </PlayButtonOverlay>
            )}

            {/* Hover play — disabled for now
          <VideoSlot ref={slotRef} />
          <LoaderOverlay $isVisible={isLoading}>
            <Spinner />
          </LoaderOverlay>
          {!isLocked && isHovering && (
            <MuteToggleButton
              type="button"
              aria-label={isMuted ? "Unmute and play audio" : "Mute"}
              onClick={onToggleMute}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <img
                src={`${STATIC_PATH}${isMuted ? "vm-mute.svg" : "vm-unmute.svg"}`}
                alt=""
              />
            </MuteToggleButton>
          )}
          */}
          </ThumbnailContainer>
          {!isLocked && (
            <Typography
              innerContent={`${secondsToMinute(
                shortStart,
              )}  -  ${secondsToMinute(shortEnd)}`}
              font={font.medium_12}
              color={vmTheme[theme].secondaryColor}
              textStyles={{
                position: "absolute",
                top: "8px",
                right: "8px",
                background: "#000",
                color: "#fff",
                padding: "2px 4px",
                borderRadius: "4px",
                zIndex: 4,
              }}
            />
          )}
          {short?.get("debug_id") && AppConfig.IS_LOCAL && (
            <Typography
              innerContent={formattedDebug}
              font={font.mediumMini}
              color={vmTheme[theme].rgbaWhite80}
              padding="2px 4px"
              textStyles={{
                position: "absolute",
                bottom: "15px",
                right: "8px",
                borderRadius: "4px",
                zIndex: 4,
              }}
            />
          )}
        </ShortCard>

        <CardMetaSection>
          <FlexDiv>
            {confidenceScore ? (
              <Typography
                content={`${Math.round(confidenceScore)}%`}
                font={font.bold_24_900}
                enableTrim={false}
                color={vmTheme[theme].shortsColor}
                textStyles={{
                  filter: isLocked ? "blur(2px)" : "none",
                  transition: "filter 0.2s ease",
                }}
              />
            ) : (
              <div />
            )}
            {socialMedia && (socialMedia.length > 0 || socialMedia.size > 0) && (
              <SocialMedia
                style={{
                  filter: isLocked ? "blur(3px)" : "none",
                  transition: "filter 0.2s ease",
                }}
              >
                {socialMedia.map((icon) => {
                  return (
                    <Links key={icon}>
                      <img
                        src={`${STATIC_PATH + icon.split(" ")[0].toLowerCase()
                          }.svg`}
                        alt={icon}
                      />
                    </Links>
                  );
                })}
                {!isLocked && permission !== 0 && (
                  <>
                    <Divider />
                    <ShortInfoFlex>
                      <CustomTooltip
                        tooltipId={`shorts-cut-tt-${shortId}`}
                        dataTooltip={content.EDIT_AS_PROJECT}
                        font={font.normal_medium_12}
                        tooltipPosition="top"
                        delayVisibilityBy={0}
                      >
                        <CutIconButton
                          type="button"
                          aria-label="Edit as project"
                          data-tooltip-id={`shorts-cut-tt-${shortId}`}
                          disabled={isLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewProject(short.get("id"));
                          }}
                        >
                          <img src={`${STATIC_PATH}vm-cut.svg`} alt="Edit" />
                        </CutIconButton>
                      </CustomTooltip>
                    </ShortInfoFlex>
                  </>
                )}
              </SocialMedia>
            )}
          </FlexDiv>
        </CardMetaSection>
        <Typography
          innerContent={`${index + 1}. ${displayTitle}`}
          font={font.boldMini_14}
          display={"block"}
          width={cardLayout.width}
          align="left"
          padding={"12px 0px 16px"}
          title={!isLocked && displayTitle}
          color={vmTheme[theme].shortsSubText}
          data-tooltip-id="short-title-tooltip"
          data-text-id="short-title-tooltip"
          textStyles={{
            filter: isLocked ? "blur(2px)" : "none",
            transition: "filter 0.2s ease",
          }}
        />
        {isLoading && (
          <CardLoaderOverlay>
            <CardLoaderSpinner />
          </CardLoaderOverlay>
        )}
      </ShortCardContainer>
    );
  },
);
ShortCardItem.displayName = "ShortCardItem";

// ─────────────────────────────────────────────────────────────────────────────
// ShortsList — owns the ONE shared <video> element
// ─────────────────────────────────────────────────────────────────────────────
const ShortsList = ({
  shorts,
  onSelectShort,
  projectDetails,
  viewedShortIds,
  feedbackMap = {},
  isFeedbackLoaded = false,
  onLike,
  onDislike,
}) => {
  /* Hover play — disabled for now
  const sharedVideoRef = useRef(null);
  const activeSlotRef = useRef(null);
  const isMutedRef = useRef(true);
  const [isMuted, setIsMuted] = useState(true);
  const seekCleanupRef = useRef(null);

  useEffect(() => {
    isMutedRef.current = isMuted;
    if (sharedVideoRef.current) {
      sharedVideoRef.current.muted = isMuted;
    }
  }, [isMuted]);
  */
  const theme = useSelector((state) => state.app.get("theme"));

  // Get sourceType and plan to control filter/sort behavior
  const userDetails = useSelector((state) => state.userDetails);
  const { sourceType } = userDetails;
  const plan = userDetails?.plan;
  const { showPricingModal } = useUpgrade();

  const handleCardClick = useCallback(
    (short) => {
      if (onSelectShort) onSelectShort(short);
    },
    [onSelectShort],
  );

  const handleFooterUpgradeClick = useCallback(() => {
    showPricingModal({ type: "longtoshort", source: "longtoshort" });
    trackerEvents(TRACKERS.LFTS_REGENRATION_UPGRADE_CLICK, {
      projectId: userDetails.projectId,
      source: "sticky-footer-upgrade-btn",
      isBoth: 1,
    });
  }, [showPricingModal, userDetails.projectId]);

  /* Hover play — disabled for now
  const handleToggleMute = useCallback((e) => {
    e.stopPropagation();

    const video = sharedVideoRef.current;
    if (!video) return;

    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    video.muted = nextMuted;
    video.volume = 1;
    if (!nextMuted) {
      video.play().catch((err) => console.error("Video play error:", err));
    }
  }, []);
  */

  // ── Derive props ───────────────────────────────────────────────────────────
  const videoSrc = useMemo(() => {
    if (!projectDetails) return null;
    const workspaceItems = projectDetails.get
      ? projectDetails.get("workspaceItems")
      : projectDetails.workspaceItems;
    if (!workspaceItems) return null;

    const videoItem = workspaceItems.valueSeq
      ? workspaceItems.valueSeq().find((item) => {
        const subType = item.get ? item.get("subType") : item.subType;
        return subType === "UPVIDEO";
      })
      : Object.values(workspaceItems).find(
        (item) => item.subType === "UPVIDEO",
      );

    if (!videoItem) return null;

    const item = videoItem.get ? videoItem.toJS() : videoItem;
    const { src } = getWorkspaceItemSource({
      item: { src: item.src, subType: item.subType, type: item.type },
    });
    return src || null;
  }, [projectDetails]);

  const videoDuration = useMemo(() => {
    if (!projectDetails) return 0;
    return projectDetails.get
      ? projectDetails.get("duration")
      : projectDetails.duration;
  }, [projectDetails]);

  // Eagerly start buffering the video for frame extraction
  useEffect(() => {
    if (videoSrc) {
      preloadExtractorVideo(videoSrc);
    }
  }, [videoSrc]);

  /* Hover play — disabled for now
  // ── Create ONE shared <video> and force-buffer on mount ───────────────────
  useEffect(() => {
    if (!videoSrc) {
      return undefined;
    }

    const video = document.createElement("video");
    video.src = videoSrc;
    video.muted = true;
    video.volume = 1;
    video.playsInline = true;
    video.preload = "metadata";

    video.style.cssText =
      "width:100%;height:100%;object-fit:cover;display:block;opacity:0;transition:opacity 0.15s ease;";

    video.load();

    sharedVideoRef.current = video;

    return () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
      sharedVideoRef.current = null;
    };
  }, [videoSrc]);

  const handleHoverStart = useCallback((slotEl, shortStart, shortId, onReady) => {
    const video = sharedVideoRef.current;
    if (!video || !slotEl) return;

    video.muted = isMutedRef.current;

    if (seekCleanupRef.current) {
      seekCleanupRef.current();
      seekCleanupRef.current = null;
    }

    video.pause();
    video.style.opacity = "0";

    if (activeSlotRef.current && video.parentNode === activeSlotRef.current) {
      activeSlotRef.current.removeChild(video);
    }

    activeSlotRef.current = slotEl;
    slotEl.appendChild(video);

    const doSeekAndPlay = () => {
      if (!Number.isFinite(shortStart)) {
        return;
      }
      video.currentTime = shortStart;

      const onSeeked = () => {
        video.volume = 1;
        video.muted = isMutedRef.current;
        video.style.opacity = "1";
        onReady();
        video.play().catch((err) => console.error("Video play error:", err));
      };

      const cleanup = () => {
        video.removeEventListener("seeked", onSeeked);
      };

      seekCleanupRef.current = cleanup;
      video.addEventListener("seeked", onSeeked, { once: true });
    };

    if (video.readyState >= 1) {
      doSeekAndPlay();
    } else {
      const onMeta = () => doSeekAndPlay();
      const cleanup = () => video.removeEventListener("loadedmetadata", onMeta);
      seekCleanupRef.current = cleanup;
      video.addEventListener("loadedmetadata", onMeta, { once: true });
    }
  }, []);

  const handleHoverEnd = useCallback(() => {
    const video = sharedVideoRef.current;
    if (!video) return;

    if (seekCleanupRef.current) {
      seekCleanupRef.current();
      seekCleanupRef.current = null;
    }

    video.pause();
    video.style.opacity = "0";

    if (activeSlotRef.current && video.parentNode === activeSlotRef.current) {
      activeSlotRef.current.removeChild(video);
    }
    activeSlotRef.current = null;
  }, []);
  */

  // ── Get sort and filter values from context ────────────────────────────────
  const { getShortsContext } = useShortsContext();
  const sortValues = getShortsContext("GET_SORT_VALUES");
  const filterValues = getShortsContext("GET_FILTER_VALUES");

  // ── Apply filter and sort ──────────────────────────────────────────────────
  const sortedShorts = useMemo(() => {
    if (!shorts) return [];

    // Convert to array
    let items;
    if (shorts.valueSeq) {
      items = shorts.valueSeq().toArray();
    } else if (Array.isArray(shorts)) {
      items = [...shorts];
    } else {
      items = Object.values(shorts);
    }

    // ── If sourceType is "shorts-dummy", skip filter & sort, return as-is ────
    if (sourceType === "shorts-dummy") {
      return items;
    }

    // ── FILTER ────────────────────────────────────────────────────────────────
    items = items.filter((short) => {
      const socialMedia = short.get
        ? short.get("socialMedia") || []
        : short.socialMedia || [];
      const clipType = short.get ? short.get("clip_type") : short.clip_type;
      const shortSourceType = (
        short.get ? short.get("sourceType") : short.sourceType
      )?.toLowerCase();

      // Filter by source type - show only "Shorts" or "shorts-dummy"
      if (
        shortSourceType &&
        shortSourceType !== "shorts" &&
        shortSourceType !== "shorts-dummy"
      ) {
        return false;
      }

      // Filter by platforms
      if (filterValues?.platforms && filterValues.platforms !== "all") {
        const platformMatch = socialMedia.some(
          (platform) =>
            platform.split(" ")[0].toLowerCase() ===
            filterValues.platforms.toLowerCase(),
        );
        if (!platformMatch) return false;
      }

      // Filter by type (sequence / non-sequence)
      if (filterValues?.type && filterValues.type !== "all") {
        // Handle both "non-sequence" and "non_sequence" formats
        const normalizedClipType = clipType?.replace(/-/g, "_");
        const normalizedFilterType = filterValues.type.replace(/-/g, "_");
        if (normalizedClipType !== normalizedFilterType) return false;
      }

      return true;
    });

    // ── SORT ──────────────────────────────────────────────────────────────────
    const sortFn = (a, b) => {
      let aVal = 0;
      let bVal = 0;

      if (sortValues?.sortBy === "score") {
        aVal =
          Number(a.get ? a.get("confidenceScore") : a.confidenceScore) || 0;
        bVal =
          Number(b.get ? b.get("confidenceScore") : b.confidenceScore) || 0;
      } else if (sortValues?.sortBy === "duration") {
        // Duration should always be endTime - startTime (seconds)
        const aStart = Number(a.get ? a.get("start") : a.start) || 0;
        const aEnd = Number(a.get ? a.get("end") : a.end) || 0;
        const bStart = Number(b.get ? b.get("start") : b.start) || 0;
        const bEnd = Number(b.get ? b.get("end") : b.end) || 0;

        aVal = Math.max(0, aEnd - aStart);
        bVal = Math.max(0, bEnd - bStart);
      }

      if (sortValues?.direction === "asc") {
        return aVal - bVal;
      }
      return bVal - aVal;
    };

    // ── Sort all items ────────────────────────────────────────────────────────
    return items.sort(sortFn);
  }, [shorts, sortValues, filterValues, sourceType]);

  if (!sortedShorts || sortedShorts.length === 0) {
    return (
      <ShortsListScrollArea>
        <EmptyContainer>
          <img src={`${STATIC_PATH}no-shorts.svg`} alt="No shorts available" />
          <Typography
            content={content.NO_SHORTS_AVAILABLE}
            color={vmTheme[theme].panelPrimaryColor}
            font={font.boldRegular_25}
            display="block"
            align="center"
            padding="20px 0px"
          />
        </EmptyContainer>
      </ShortsListScrollArea>
    );
  }

  const shouldShowFooter = plan === PLAN.LITE && sortedShorts.length > 3;

  return (
    <>
      <ShortsListScrollArea onTouchMove={(e) => e.stopPropagation()}>
        <ShortsGridContainer>
          {sortedShorts.map((short, index) => {
            const shortId = short.get ? short.get("id") : short.id;
            return (
              <ShortCardItem
                key={shortId}
                short={short}
                index={index}
                onSelectShort={handleCardClick}
                videoSrc={videoSrc}
                videoDuration={videoDuration}
                isViewed={viewedShortIds?.has(shortId)}
                feedbackType={feedbackMap[shortId]?.feedbackType || null}
                isFeedbackLoaded={isFeedbackLoaded}
                onLike={onLike}
                onDislike={onDislike}
                isLocked={false}
              />
            );
          })}
        </ShortsGridContainer>
      </ShortsListScrollArea>
      {shouldShowFooter && (
        <StickyFooterBanner>
          <FooterContent>
            <Action
              width={"512px"}
              text="Regenerate Shorts"
              borderColor={"#BCACFF"}
              className={"regenerate-shorts-button"}
              fontColor={"#5028FF"}
              borderWidth={"1px"}
              borderRadius={"12px"}
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "24px",
              }}
              font={font.boldRegular_25}
              marginBottom={"0px"}
              marginTop={"0px"}
              background={"linear-gradient(91deg, #F6F2FF 0%, #EDE4FF 100%)"}
              customBeforeIcon={`${STATIC_PATH}ai.svg`}
              onClick={() => handleFooterUpgradeClick()}
            />
            <Typography
              innerContent="New hooks, new angles, new aspect ratios - every run."
              font={font.normal_medium_12}
              color="#64748B"
            />
          </FooterContent>
        </StickyFooterBanner>
      )}
    </>
  );
};

ShortCardItem.propTypes = {
  short: PropTypes.oneOfType([PropTypes.object]),
  onSelectShort: PropTypes.func,
  videoSrc: PropTypes.string,
  index: PropTypes.number,
  isViewed: PropTypes.bool,
  feedbackType: PropTypes.oneOf(["like", "dislike", null]),
  isFeedbackLoaded: PropTypes.bool,
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
  isLocked: PropTypes.bool,
};

ShortCardItem.defaultProps = {
  short: null,
  onSelectShort: () => { },
  videoSrc: null,
  isLocked: false,
};

ShortsList.propTypes = {
  shorts: PropTypes.object,
  onSelectShort: PropTypes.func,
  projectDetails: PropTypes.object,
  viewedShortIds: PropTypes.instanceOf(Set),
  feedbackMap: PropTypes.object,
  isFeedbackLoaded: PropTypes.bool,
  onLike: PropTypes.func,
  onDislike: PropTypes.func,
};

ShortsList.defaultProps = {
  shorts: null,
  onSelectShort: () => { },
  projectDetails: null,
};

export default React.memo(ShortsList);
